import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import TourV2SearchGroupItems from 'components/Search/TourV2/TourV2SearchGroupItems'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import LineUsersMultiIcon from 'LuxKit/Icons/line/LineUsersMultiIcon'
import { TOURS_GROUP_TYPE } from 'constants/tours'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

export function getTourGroupTypesList(intl: IntlShape): Array<App.Tours.TourItemGroupType> {
  return [
    {
      id: TOURS_GROUP_TYPE.isSmallGroup,
      name: intl.formatMessage({
        id: 'sljDmHvaHu',
        defaultMessage: 'Small Group Tours',
        description: 'Label for small group tours filter option',
      }),
      subtitle: intl.formatMessage({
        id: '6LKvZ6sRm/',
        defaultMessage: 'Join a group of up to 24 travellers',
        description: 'Description for small group tours filter option',
      }),
      icon: <LineUsersAltIcon />,
    },
    {
      id: TOURS_GROUP_TYPE.isLargeGroup,
      name: intl.formatMessage({
        id: 'ZNiLgkKTBR',
        defaultMessage: 'Large Group Tours',
        description: 'Label for large group tours filter option',
      }),
      subtitle: intl.formatMessage({
        id: 'uQcy/zhnNB',
        defaultMessage: 'Join a group of 24+ travellers',
        description: 'Description for large group tours filter option',
      }),
      icon: <LineUsersMultiIcon />,
    },
    {
      id: TOURS_GROUP_TYPE.isPrivateRequest,
      name: intl.formatMessage({
        id: 'sMrJGinNsJ',
        defaultMessage: 'Private Requests Available',
        description: 'Label for private requests tour filter option',
      }),
      subtitle: intl.formatMessage({
        id: 'J5O2oO77YK',
        defaultMessage: 'Travel exclusively with your family and friends',
        description: 'Description for private requests tour filter option',
      }),
      icon: <LineGuidedtoursIcon />,
    },
    {
      id: TOURS_GROUP_TYPE.isFamilyFriendly,
      name: intl.formatMessage({
        id: '0uXyrAKFpW',
        defaultMessage: 'Family Friendly',
        description: 'Label for family friendly tour filter option',
      }),
      subtitle: intl.formatMessage({
        id: '+fXTPQ+PX+',
        defaultMessage: 'Great for both adults and children',
        description: 'Description for family friendly tour filter option',
      }),
      icon: <LineKidIcon />,
    },
  ]
}

export const tourGroupTypeIds = [
  TOURS_GROUP_TYPE.isSmallGroup,
  TOURS_GROUP_TYPE.isLargeGroup,
  TOURS_GROUP_TYPE.isPrivateRequest,
  TOURS_GROUP_TYPE.isFamilyFriendly,
]

export const getTourGroupSelectedText = (tourGroupTypes: Array<App.Tours.TourGroupType>, intl: IntlShape) => {
  return getTourGroupTypesList(intl).reduce<Array<string>>((acc, tourGroupType) => {
    if (tourGroupTypes.includes(tourGroupType.id)) acc.push(tourGroupType.name)
    return acc
  }, []).join(' or ') // TODO - i18n: "or" conjunction needs to be localised
}

interface Props {
  isToggled: boolean
  onGroupToggle: (...args: any) => void
  onApply: (...args: any) => void
}

function TourV2SearchGroupSelect(props: Props) {
  const { isToggled, onGroupToggle, onApply } = props
  const intl = useIntl()

  const { tourGroupTypes = [] } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const tourGroupTypesList = useMemo(() => getTourGroupTypesList(intl), [intl])

  const resetGroupType = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SELECT_ALL_TOUR_GROUP_TYPE })
  }, [globalSearchDispatch])

  const tourGroupTypeSelectedText = useMemo(() => getTourGroupSelectedText(tourGroupTypes, intl), [tourGroupTypes, intl])

  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const groupLabel = intl.formatMessage({
    id: 'XG101jiWOs',
    defaultMessage: 'Type of group',
    description: 'Label for tour group type search filter field',
  })
  const groupPlaceholder = intl.formatMessage({
    id: 'JWuXtmWukD',
    defaultMessage: 'e.g Small Group Tours, Private Requests Available...',
    description: 'Placeholder text for tour group type search filter field',
  })
  const resetLabel = intl.formatMessage({
    id: 'kxACepk+7S',
    defaultMessage: 'Reset',
    description: 'Reset button text for tour group type filter',
  })
  const applyLabel = intl.formatMessage({
    id: '6MxtwyBgjT',
    defaultMessage: 'Apply',
    description: 'Apply button text for tour group type filter',
  })

  return (<>
    <SearchFormField
      ref={dropdownTriggerRef}
      onClick={onGroupToggle}
      label={groupLabel}
      placeholder={groupPlaceholder}
      value={tourGroupTypeSelectedText}
    />
    <DropdownList
      size="fill-anchor"
      anchorRef={dropdownTriggerRef}
      triggerRef={dropdownTriggerRef}
      open={isToggled}
      onClose={onGroupToggle}
      secondaryActionProps={{
        kind: 'tertiary',
        children: resetLabel,
        onClick: resetGroupType,
        'data-testid': 'search-tour-grouptype-reset-button',
      }}
      primaryActionProps={{
        children: applyLabel,
        onClick: onApply,
        'data-testid': 'search-tour-grouptype-apply-button',
      }}
    >
      {isToggled && <TourV2SearchGroupItems groupTypes={tourGroupTypesList} />}
    </DropdownList>
  </>

  )
}

export default TourV2SearchGroupSelect
